import React, { useState } from 'react';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { observer } from 'mobx-react';
import ColumnSelector from 'ats/components/column-selector/ColumnSelector';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { clientStore } from './ClientStore';
import AvatarImage from 'ats/components/avatar-image/AvatarImage';
import moment from 'moment';
import BulkActionsBar from 'ats/components/bulk-actions-bar/BulkActionsBar';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { modalStore } from 'components/layout/modals/ModalStore';
import { ClientEdit } from './components/ClientEdit';
import AtsTableSkeleton from 'ats/components/ats-table-skeleton/AtsTableSkeleton';

function ClientsTable({
	data,
	onClickRow,
	sort,
	onSort,
	onSuccess,
	activeRowId,
	visibleColumns,
	onVisibleColumnsChange,
	isEmpty,
	emptyState,
	loading
}) {
	const [selectedIds, setSelectedIds] = useState([]);

	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;
		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const resolvedVisibleColumns =
		visibleColumns || clientStore.defaultTableColumnsOptions;

	const shouldShowColumn = column =>
		resolvedVisibleColumns.some(
			visibleColumn => visibleColumn.value === column
		);

	const handleSelectAll = e => {
		if (e.target.checked) {
			setSelectedIds(data.map(row => row.id));
		} else {
			setSelectedIds([]);
		}
	};

	const handleSelectRow = (e, rowId) => {
		e.stopPropagation();
		if (e.target.checked) {
			setSelectedIds(prev => [...prev, rowId]);
		} else {
			setSelectedIds(prev => prev.filter(id => id !== rowId));
		}
	};

	const isAllSelected = data.length > 0 && selectedIds.length === data.length;
	const isPartiallySelected =
		selectedIds.length > 0 && selectedIds.length < data.length;

	const handleBulkDelete = () => {
		dialogStore.openDialog(
			{
				title: 'Delete Clients',
				message: `Are you sure you want to delete ${selectedIds.length} client(s)? This action cannot be undone.`,
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			async () => {
				try {
					const result = await clientStore.bulkDelete(selectedIds);
					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Clients Deleted',
							`Successfully deleted ${result.data.deleted} client(s)`
						);
						setSelectedIds([]);
						onSuccess?.();
					} else {
						notificationStore.pushErrorNotification(
							'Delete Failed',
							'Failed to delete clients'
						);
					}
				} catch (error) {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete some clients'
					);
				}
			}
		);
	};

	const getBulkActions = () => {
		return [
			{
				label: 'Delete',
				isDanger: true,
				onClick: handleBulkDelete
			}
		];
	};

	const handleClientEdit = row => {
		modalStore.openModal(
			{
				title: 'Edit Client',
				size: '2xl',
				position: 'center',
				saveButtonLabel: 'Save'
			},
			<ClientEdit client={row} handleSuccess={onSuccess} />
		);
	};

	const handleClientDelete = row => {
		dialogStore.openDialog(
			{
				title: 'Delete Client',
				message: `Are you sure you want to delete "${row.name}"? This action cannot be undone.`,
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			async () => {
				try {
					const result = await clientStore.delete(row.id);
					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Client Deleted',
							'The client has been successfully deleted.'
						);
						onSuccess?.();
					} else {
						notificationStore.pushErrorNotification(
							'Delete Failed',
							'Failed to delete the client'
						);
					}
				} catch (error) {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete the client'
					);
				}
			}
		);
	};

	const getMenuItems = row => [
		{
			label: 'Edit',
			img: '/ats/icons/edit-icon.svg',
			onClick: () => handleClientEdit(row)
		},
		{
			label: 'Delete',
			img: '/ats/icons/trash-icon.svg',
			className: 'is-danger',
			onClick: () => handleClientDelete(row)
		}
	];

	const getIndustryLabel = row => {
		return row.industry?.name || '';
	};

	const isProfileOpen =
		drawerStore.isOpen &&
		[
			drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
			drawerStore.DRAWER_IDS.CLIENT_PROFILE,
			drawerStore.DRAWER_IDS.APPLICANT_PROFILE
		].includes(drawerStore.drawerId);

	const isClientProfileOpen =
		drawerStore.isOpen &&
		drawerStore.drawerId === drawerStore.DRAWER_IDS.CLIENT_PROFILE;

	const renderTableHeader = () => (
		<thead>
			<tr>
				<th className="checkbox-column">
					<div className="checkbox-cell">
						<input
							type="checkbox"
							checked={isAllSelected}
							ref={input => {
								if (input) {
									input.indeterminate = isPartiallySelected;
								}
							}}
							onChange={handleSelectAll}
						/>
					</div>
				</th>

				{shouldShowColumn('name') && (
					<th className="is-sortable" onClick={() => handleHeaderClick('name')}>
						Name {isColumnSorted('name')}
					</th>
				)}
				{isProfileOpen ? null : (
					<>
						{shouldShowColumn('industry') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('industry')}>
								Industry {isColumnSorted('industry')}
							</th>
						)}
						{shouldShowColumn('size') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('size')}>
								Size {isColumnSorted('size')}
							</th>
						)}
						{shouldShowColumn('accountManager') && (
							<th
								className="is-sortable has-text-centered account-manager-column-header"
								onClick={() => handleHeaderClick('accountManager.name')}>
								<div className="is-flex is-align-items-center is-justify-content-center account-manager-th-inner">
									<span className="account-manager-header-label">
										Account Manager
									</span>
									{isColumnSorted('accountManager.name')}
								</div>
							</th>
						)}
						{shouldShowColumn('primaryContact') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('$primaryContact')}>
								Primary Contact {isColumnSorted('$primaryContact')}
							</th>
						)}
						{shouldShowColumn('openVacancies') && (
							<th
								className="is-sortable has-text-centered open-vacancies-column-header"
								onClick={() => handleHeaderClick('$openVacancies')}>
								<div className="is-flex is-align-items-center is-justify-content-center open-vacancies-th-inner">
									<span className="open-vacancies-header-label">
										Open Vacancies
									</span>
									{isColumnSorted('$openVacancies')}
								</div>
							</th>
						)}
						{shouldShowColumn('openJobs') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('$openJobs')}>
								Open Jobs {isColumnSorted('$openJobs')}
							</th>
						)}
						{shouldShowColumn('daysOpenAvg') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('$daysOpenAvg')}>
								Days Open Avg {isColumnSorted('$daysOpenAvg')}
							</th>
						)}
						{shouldShowColumn('createdAt') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('createdAt')}>
								Created At {isColumnSorted('createdAt')}
							</th>
						)}
						{shouldShowColumn('updatedAt') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('updatedAt')}>
								Updated At {isColumnSorted('updatedAt')}
							</th>
						)}
					</>
				)}
				<th className="has-text-right action-btn">
					{isProfileOpen ? null : (
						<ColumnSelector
							target={props => (
								<button type="button" className="action-btn" {...props}>
									<img src="/ats/icons/plus-icon.svg" alt="Add button" />
								</button>
							)}
							value={resolvedVisibleColumns}
							options={clientStore.tableColumnsOptions}
							onChange={values => onVisibleColumnsChange?.(values)}
						/>
					)}
				</th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		const industryLabel = getIndustryLabel(row);
		const isSelected = selectedIds.includes(row.id);

		return (
			<tr
				key={row.id}
				className={`${isClientProfileOpen && String(activeRowId) === String(row.id) ? 'is-active' : ''} ${isSelected ? 'is-selected' : ''}`}
				onClick={event => {
					event.stopPropagation();

					if (!['BUTTON', 'INPUT'].includes(event.target.tagName)) {
						onClickRow?.(row);
					}
				}}>
				<td className="checkbox-column">
					<div className="checkbox-cell">
						<input
							type="checkbox"
							checked={isSelected}
							onChange={e => handleSelectRow(e, row.id)}
						/>
					</div>
				</td>
				{shouldShowColumn('name') && (
					<td className="name-column">
						<div className="name-cell">
							<AvatarImage
								src={row.imageUrl}
								alt="Client"
								placeholderFontSize={12}
								placeholderText={row.name?.charAt(0) || 'C'}
							/>
							{row.name ? row.name : ''}
						</div>
					</td>
				)}
				{isProfileOpen ? null : (
					<>
						{shouldShowColumn('industry') && <td>{industryLabel}</td>}
						{shouldShowColumn('size') && <td>{row.size}</td>}
						{shouldShowColumn('accountManager') && (
							<td className="accountManager-column">
								<div className="account-manager-cell">
									{row.accountManager && (
										<UserProfileAvatar
											imageUrl={row.accountManager?.imageUrl}
											name={row.accountManager?.name}
											size={16}
										/>
									)}
								</div>
							</td>
						)}
						{shouldShowColumn('primaryContact') && (
							<td className="has-text-centered">{row.primaryContact ?? '-'}</td>
						)}
						{shouldShowColumn('openVacancies') && (
							<td className="has-text-centered">{row.openVacancies ?? 0}</td>
						)}
						{shouldShowColumn('openJobs') && (
							<td className="has-text-centered">{row.openJobs ?? 0}</td>
						)}
						{shouldShowColumn('daysOpenAvg') && (
							<td className="has-text-centered">{row.daysOpenAvg ?? '-'}</td>
						)}
						{shouldShowColumn('createdAt') && (
							<td className="has-text-centered">
								{moment(row.createdAt).format('lll')}
							</td>
						)}
						{shouldShowColumn('updatedAt') && (
							<td className="has-text-centered">
								{moment(row.updatedAt).format('lll')}
							</td>
						)}
					</>
				)}
				<td className="has-text-right action-btn">
					<HoverMenu
						id={row.id}
						position="left"
						buttonIcon={
							<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
						}
						items={getMenuItems(row)}
					/>
				</td>
			</tr>
		);
	};

	return (
		<>
			<BulkActionsBar
				selectedCount={selectedIds.length}
				actions={getBulkActions()}
				onClearSelection={() => setSelectedIds([])}
			/>
			<div className={`ats-table-scroll${isEmpty ? ' is-empty-results' : ''}`}>
				<table className="table is-fullwidth">
					{renderTableHeader()}
					{loading && data.length === 0 ? (
						<AtsTableSkeleton
							columns={Math.max(visibleColumns?.length || 0, 4)}
						/>
					) : (
						!isEmpty && <tbody>{data.map(renderTableRow)}</tbody>
					)}
				</table>
				{isEmpty && <div className="clients-empty">{emptyState}</div>}
			</div>
		</>
	);
}

export default observer(ClientsTable);
